import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClientJsonpModule,
  HttpClientModule,
} from '@angular/common/http';
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { ExceptionInterceptor } from '@boma/shared/common';
import * as fromLocations from '@boma/supplier-portal/shared/state';
import * as fromVehicles from '@boma/supplier-portal/shared/state';
import {
  LocationsEffects,
  LocationsFacade,
  VehiclesFacade,
} from '@boma/supplier-portal/shared/state';
import { UiElementsModule } from '@boma/ui-elements';
import { WebsiteSharedUiModule } from '@boma/website-shared-ui';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RippleModule } from 'primeng/ripple';
import { VirtualScrollerModule } from 'primeng/virtualscroller';
import { GetFirstAvailableImageForSizePipe } from '../../../shared/common/src/lib/pipes/show-entity-image.pipe';
import { OrganizationFacade } from '../../../supplier-portal/organization/src/lib/+state/organization.facade';
import { AppRightMenuComponent } from './app-right-menu/app-right-menu.component';
import { BomaCategorySelectorComponent } from './boma-category-selector/boma-category-selector.component';
import { BomaCustomerSelectorComponent } from './boma-customer-selector/boma-customer-selector.component';
import { BomaDefinitionCreatorComponent } from './boma-definition-creator/boma-definition-creator.component';
import { BomaDefinitionSelectorComponent } from './boma-definition-selector/boma-definition-selector.component';
import { BomaDepartmentSelectorComponent } from './boma-department-selector/boma-department-selector.component';
import { BomaLocationFormComponent } from './boma-location-form/boma-location-form.component';
import { BomaLocationSearchComponent } from './boma-location-search/boma-location-search.component';
import { BomaNoResultsComponent } from './boma-no-results/boma-no-results.component';
import { BomaOrdersTableComponent } from './boma-orders-table/boma-orders-table.component';
import { BomaPaginationComponent } from './boma-pagination/boma-pagination.component';
import { BomaPrintFooterComponent } from './boma-print-footer/boma-print-footer.component';
import { CardImageComponent } from './card-image/card-image.component';
import { CardMenuComponent } from './card-menu/card-menu.component';
import { ChartsComponent } from './charts/charts.component';
import { CheckboxListComponent } from './checkbox-list/checkbox-list/checkbox-list.component';
import { CreateNewOrderComponent } from './create-new-order/create-new-order.component';
import { CrudTableComponent } from './crud-table/crud-table.component';
import { HasPermissionsDirective } from './directives/has-permissions.directive';
import { PermissionCheckerDirective } from './directives/permission-checker.directive';
import { FilterBlockComponent } from './filter-block/filter-block.component';
import { InvoiceComponent } from './invoice/invoice.component';
import { LocationDateRangeSelectorComponent } from './location-date-range-selector/location-date-range-selector.component';
import { LocationPickerMapComponent } from './location-picker-map/location-picker-map.component';
import { LocationProductPricingAddOrUpdateComponent } from './location-product-pricing-add-or-update/location-product-pricing-add-or-update.component';
import { MapMarkersComponent } from './map-markers/map-markers.component';
import { NoteCreateComponentComponent } from './note-create-component/note-create-component.component';
import { NoteListComponentComponent } from './note-list-component/note-list-component.component';
import { PhoneNumberInputComponent } from './phone-number-input/phone-number-input.component';
import { GetTaxValueOfPricePipe } from './pipes/get-tax-value-of-price.pipe';
import { GetValueBeforeTaxPipe } from './pipes/get-value-before-tax.pipe';
import { LocalePipe } from './pipes/locale.pipe';
import { ShorthandNumberPipe } from './pipes/shorthand-number.pipe';
import { PortalFooterComponent } from './portal-footer/portal-footer.component';
import { PortalMapComponent } from './portal-map/portal-map.component';
import { PortalMenuItemComponent } from './portal-menu-item/portal-menu-item.component';
import { PortalMenuComponent } from './portal-menu/portal-menu.component';
import { PortalRightMenuComponent } from './portal-right-menu/portal-right-menu.component';
import { PortalSwitchDirectoryComponent } from './portal-switch-directory/portal-switch-directory.component';
import { PortalTopBarComponent } from './portal-top-bar/portal-top-bar.component';
import { ProductImageUploadComponent } from './product-image-upload/product-image-upload.component';
import { ProductSearchComponent } from './product-search/product-search.component';
import { ProductSelectorTableComponent } from './product-selector-table/product-selector-table.component';
import { ProductSelectorComponent } from './product-selector/product-selector.component';
import { ProgressSpinnerComponent } from './progress-spinner/progress-spinner.component';
import { ReceiptComponent } from './receipt/receipt.component';
import { SearchEntityUsersComponent } from './search-entity-users/search-entity-users.component';
import { StockLowStockComponent } from './stock-low-stock/stock-low-stock.component';
import { StockProductSelectorComponent } from './stock-product-selector/stock-product-selector.component';
import { StockRulesComponent } from './stock-rules/stock-rules.component';
import { TaxDefinitionsSelectorComponent } from './tax-definitions-selector/tax-definitions-selector.component';
import { ToastComponent } from './toast/toast.component';
import { UnauthedComponent } from './unauthed/unauthed.component';
import { UserNotificationsComponent } from './user-notifications/user-notifications.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    UiElementsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    VirtualScrollerModule,
    WebsiteSharedUiModule,
    GoogleMapsModule,
    RippleModule,
    StoreModule.forFeature(
      fromLocations.LOCATIONS_FEATURE_KEY,
      fromLocations.locationReducer
    ),
    StoreModule.forFeature(
      fromVehicles.VEHICLES_FEATURE_KEY,
      fromVehicles.vehicleReducer
    ),
    EffectsModule.forFeature([LocationsEffects]),
  ],
  declarations: [
    PortalMenuComponent,
    CrudTableComponent,
    ProgressSpinnerComponent,
    ChartsComponent,
    PortalFooterComponent,
    PortalMenuItemComponent,
    PortalTopBarComponent,
    PortalRightMenuComponent,
    ToastComponent,
    LocalePipe,
    GetFirstAvailableImageForSizePipe,
    HasPermissionsDirective,
    GetValueBeforeTaxPipe,
    ProductSelectorComponent,
    GetTaxValueOfPricePipe,
    UserNotificationsComponent,
    PortalSwitchDirectoryComponent,
    PortalMapComponent,
    LocationPickerMapComponent,
    ProductImageUploadComponent,
    LocationDateRangeSelectorComponent,
    CreateNewOrderComponent,
    ProductSearchComponent,
    StockProductSelectorComponent,
    CardImageComponent,
    InvoiceComponent,
    ReceiptComponent,
    BomaPrintFooterComponent,
    TaxDefinitionsSelectorComponent,
    SearchEntityUsersComponent,
    ProductSelectorComponent,
    BomaCategorySelectorComponent,
    BomaDepartmentSelectorComponent,
    BomaCustomerSelectorComponent,
    ProductSelectorTableComponent,
    BomaDefinitionSelectorComponent,
    LocationProductPricingAddOrUpdateComponent,
    FilterBlockComponent,
    BomaDefinitionCreatorComponent,
    BomaNoResultsComponent,
    AppRightMenuComponent,
    BomaOrdersTableComponent,
    HasPermissionsDirective,
    PermissionCheckerDirective,
    BomaLocationFormComponent,
    CardMenuComponent,
    BomaPaginationComponent,
    MapMarkersComponent,
    NoteCreateComponentComponent,
    NoteListComponentComponent,
    BomaLocationSearchComponent,
    CheckboxListComponent,
    UnauthedComponent,
    PhoneNumberInputComponent,
    ShorthandNumberPipe,
    StockRulesComponent,
    StockLowStockComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ExceptionInterceptor,
      multi: true,
    },
    MessageService,
    ConfirmationService,
    LocalePipe,
    GetFirstAvailableImageForSizePipe,
    GetValueBeforeTaxPipe,
    GetTaxValueOfPricePipe,
    OrganizationFacade,
    LocationsFacade,
    VehiclesFacade,
  ],
  exports: [
    VirtualScrollerModule,
    UiElementsModule,
    HasPermissionsDirective,
    PortalMenuComponent,
    ProgressSpinnerComponent,
    ChartsComponent,
    PortalTopBarComponent,
    PortalFooterComponent,
    PortalRightMenuComponent,
    CrudTableComponent,
    UserNotificationsComponent,
    ToastComponent,
    LocalePipe,
    GetFirstAvailableImageForSizePipe,
    GetValueBeforeTaxPipe,
    GetTaxValueOfPricePipe,
    ShorthandNumberPipe,
    PortalSwitchDirectoryComponent,
    PortalMapComponent,
    LocationPickerMapComponent,
    ProductSelectorComponent,
    ProductImageUploadComponent,
    LocationDateRangeSelectorComponent,
    CreateNewOrderComponent,
    ProductSearchComponent,
    StockProductSelectorComponent,
    CardImageComponent,
    InvoiceComponent,
    ReceiptComponent,
    BomaPrintFooterComponent,
    TaxDefinitionsSelectorComponent,
    SearchEntityUsersComponent,
    BomaCategorySelectorComponent,
    BomaDepartmentSelectorComponent,
    BomaCustomerSelectorComponent,
    ProductSelectorTableComponent,
    BomaDefinitionSelectorComponent,
    BomaDefinitionCreatorComponent,
    FilterBlockComponent,
    BomaNoResultsComponent,
    AppRightMenuComponent,
    BomaOrdersTableComponent,
    WebsiteSharedUiModule,
    HasPermissionsDirective,
    PermissionCheckerDirective,
    BomaLocationFormComponent,
    CardMenuComponent,
    MapMarkersComponent,
    BomaPaginationComponent,
    NoteCreateComponentComponent,
    NoteListComponentComponent,
    BomaLocationSearchComponent,
    CheckboxListComponent,
    UnauthedComponent,
    PhoneNumberInputComponent,
    StockRulesComponent,
    StockLowStockComponent,
  ],
})
export class UiModule {}
